import React, { useState, useEffect } from 'react';
import FinanceLayout from '../../../../../../layout/components/FinanceLayout';
import MonthlyDateSelect from '../../../../../../components/AppInput/MonthlyDateSelect';
import { CircularProgress, Dialog, DialogContent } from '@mui/material';
import gstReportServices from '../gstReportServices'; // Ensure this contains your GSTR2 endpoints

function GSTR2Page() {

    const [loading, setLoadingStatus] = useState(false);
    const [auditInfo, setAuditInfo] = useState();
    const [summary, setSummary] = useState();
    const [netClaimableItc, setNetClaimableItc] = useState();
    const [filter, setFilter] = useState(null);

    const [dialogStatus, setDialogStatus] = useState(false);
    const [jsonData, setJsonData] = useState(null);

    const getVoucherList = () => {
        setLoadingStatus(true);
        // Consuming your new backend processGstr2Data calculation engine
        gstReportServices.getGSTR2Summary(filter).then(res => {
            setSummary(res.data.summary);
            setAuditInfo(res.data.auditInfo);
            setNetClaimableItc(res.data.netTaxLiability); // Maps to net claimable input credit
        }).catch(error => {
            console.error("GSTR2 Summary loading failed:", error);
        }).finally(() => {
            setLoadingStatus(false);
        });
    };
    
    useEffect(() => {
        if (filter) {
            getVoucherList();
        }
    }, [filter]);

    const getJSON = () => {
        setDialogStatus(true);
        gstReportServices.getGSTR2Json(filter).then(res => {
            setJsonData(res.payload);
        }).catch(error => {
            console.error("GSTR2 JSON export failed:", error);
        });
    };

    return (
        <FinanceLayout>
            <div className='row my-2'>
                <div className='col-md-6'>
                    <div className="fw-bold h5 py-2">GSTR2 Summary (Inward Supplies / ITC)</div>
                </div>

                <div className='col-md-6 '>
                    <div className="bg-white p-2 d-flex justify-content-end align-items-center gap-2">
                        <MonthlyDateSelect filter={filter} setFilter={setFilter} />
                        <button className='btn btn-outline-success' onClick={getJSON}>Get JSON</button>
                    </div>
                </div>

                <Dialog
                    open={dialogStatus}
                    keepMounted
                    onClose={() => setDialogStatus(false)}
                    fullWidth={true}
                    maxWidth="lg"
                >
                    <DialogContent>
                        <div className="mb-2 fw-bold text-secondary">Raw GSTR-2 Government Payload Preview:</div>
                        <pre className="bg-light p-3 border rounded text-dark" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {JSON.stringify(jsonData, null, 2)}
                        </pre>
                    </DialogContent>
                </Dialog>
            </div>

            {loading && 
              <div className='text-center mt-5'>
                <CircularProgress />
              </div>
            }

            {!loading && auditInfo && summary && netClaimableItc &&
                <div className='row my-2'>
                    {/* --- AUDIT INFORMATION TRIANGULATION GRID --- */}
                    <div className='col-md-12'>
                        <table className='table table-bordered table-sm mt-3'>
                            <thead>
                                <tr className='table-dark'>
                                    <th>Total Inward Vouchers</th>
                                    <th>{auditInfo.totalVouchers}</th>
                                </tr>
                                <tr className='table-primary'>
                                    <td>Included In ITC Computation</td>
                                    <td>{auditInfo.includedInReturn}</td>
                                </tr>
                                <tr className='table-warning'>
                                    <td>Excluded from Return Engine</td>
                                    <td></td>
                                </tr>
                                <tr className='table-info'>
                                    <td><span className='ms-5'>Draft Inward Vouchers Excluded</span></td>
                                    <td>{auditInfo.draftVouchersExcluded}</td>
                                </tr>
                                <tr className='table-info'>
                                    <td><span className='ms-5'>Canceled Inward Vouchers</span></td>
                                    <td>{auditInfo.canceledVouchersExcluded}</td>
                                </tr>
                            </thead>
                        </table>
                    </div>

                    {/* --- STATUTORY GSTR-2 PARTICULARS TABLE --- */}
                    <div className='col-md-12 mt-2'>
                        <table className='table table-bordered table-sm mt-3'>
                            <thead>
                                <tr className='table-dark text-center'>
                                    <td>#</td>
                                    <td>Particulars (Inward Streams)</td>
                                    <td>Voucher Count</td>
                                    <td>Taxable Amount</td>
                                    <td>CGST (Input)</td>
                                    <td>SGST (Input)</td>
                                    <td>IGST (Input)</td>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th className="text-center">1</th>
                                    <th>Inward Supplies from Registered Suppliers - 3, 4A</th>
                                    <td className="text-center">{summary.b2b.count}</td>
                                    <td className="text-end">{summary.b2b.taxable.toFixed(2)}</td>
                                    <td className="text-end text-success">{summary.b2b.cgst.toFixed(2)}</td>
                                    <td className="text-end text-success">{summary.b2b.sgst.toFixed(2)}</td>
                                    <td className="text-end text-success">{summary.b2b.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">2</th>
                                    <th>Inward Supplies from Unregistered Suppliers (RCM) - 4B</th>
                                    <td className="text-center">{summary.b2bur.count}</td>
                                    <td className="text-end">{summary.b2bur.taxable.toFixed(2)}</td>
                                    <td className="text-end text-success">{summary.b2bur.cgst.toFixed(2)}</td>
                                    <td className="text-end text-success">{summary.b2bur.sgst.toFixed(2)}</td>
                                    <td className="text-end text-success">{summary.b2bur.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">3</th>
                                    <th>Debit Notes issued to Registered Vendors - 5</th>
                                    <td className="text-center">{summary.cdnr.count}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.taxable.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.cgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.sgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnr.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">4</th>
                                    <th>Debit Notes issued to Unregistered Vendors - 5</th>
                                    <td className="text-center">{summary.cdnur.count}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.taxable.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.cgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.sgst.toFixed(2)}</td>
                                    <td className="text-end text-danger">-{summary.cdnur.igst.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-center">5</th>
                                    <th>Nil Rated / Exempt Inward Supplies - 7</th>
                                    <td className="text-center">{summary.nilRatedInc.count}</td>
                                    <td className="text-end">{summary.nilRatedInc.taxable.toFixed(2)}</td>
                                    <td className="text-end">{summary.nilRatedInc.cgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.nilRatedInc.sgst.toFixed(2)}</td>
                                    <td className="text-end">{summary.nilRatedInc.igst.toFixed(2)}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr className='table-info fw-bold'>
                                    <th colSpan={3}><span className='float-end me-5'>Net Available Input Tax Credit (ITC)</span></th>
                                    <th className="text-end">{netClaimableItc.taxable.toFixed(2)}</th>
                                    <th className="text-end text-dark">{netClaimableItc.cgst.toFixed(2)}</th>
                                    <th className="text-end text-dark">{netClaimableItc.sgst.toFixed(2)}</th>
                                    <th className="text-end text-dark">{netClaimableItc.igst.toFixed(2)}</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            }
        </FinanceLayout>
    );
}

export default GSTR2Page;